// Player movement: a port of Wolfenstein: Enemy Territory 2.60b bg_pmove.c /
// bg_slidemove.c, with the ETJump mod's movement rules layered on top
// (see MapRules.mod). Prone, weapons, animations and dodge (dead code in ET)
// are not ported.
//
// Every expression follows the C types of the original: floats are rounded
// with f() (Math.fround) after each float operation, double literals are kept
// in double until the result is stored. With rules.mod = 'vanilla' the port is
// checked tick by tick against the original C code compiled from the GPL
// source (tools/refharness), on SSE2-style IEEE float arithmetic.
//
// This module is pure and deterministic so that a future multiplayer server
// can run exactly the same code as the client.

import type { TraceResult } from '../cm/types';
import { M_PI_F, f, fangleVectors, fcross, fdot, flength, fma3, fnormalize, fnormalize2, fscale, fvectoangles, rintf } from '../f32';
import { ANGLE2SHORT, copy, type Vec3 } from '../math';
import { CONTENTS_SLIME, CONTENTS_SOLID, MASK_PLAYERSOLID, MASK_WATER, SURF_LADDER, SURF_NODAMAGE, SURF_SLICK } from '../surfaceflags';

// --- constants (bg_public.h / bg_local.h / bg_pmove.c) ---
export const ENTITYNUM_NONE = 1023;
export const ENTITYNUM_WORLD = 1022;
export const JUMP_VELOCITY = 270;
export const STEPSIZE = 18;
/** double literal in bg_local.h; compared against float normals */
export const MIN_WALK_NORMAL = 0.7;
/** double literal; converted to float when passed to PM_ClipVelocity */
export const OVERCLIP = 1.001;
const OVERCLIP_F = f(OVERCLIP);
export const DEFAULT_GRAVITY = 800;
export const DEFAULT_SPEED = 320;
export const SPRINTTIME = 20000;
export const JUMP_DELAY_TIME = 850;
export const DEFAULT_VIEWHEIGHT = 40;
export const CROUCH_VIEWHEIGHT = 16;
export const PRONE_VIEWHEIGHT = -8;
export const PLAYER_MINS: Vec3 = [-18, -18, -24];
export const PLAYER_MAXS: Vec3 = [18, 18, 48];
/** bg_misc.c playerlegsProneMins / Maxs: the legs box trailing a prone player */
const LEGS_MINS: Vec3 = [-13.5, -13.5, -24];
const LEGS_MAXS: Vec3 = [13.5, 13.5, f(-14.4)];
const MAX_CLIP_PLANES = 5;
const TRACE_LADDER_DIST = 48;

// float globals in bg_pmove.c
const pm_stopspeed = 100;
const pm_accelerate = 10;
const pm_airaccelerate = 1;
const pm_wateraccelerate = 4;
const pm_slagaccelerate = 2;
const pm_friction = 6;
const pm_waterfriction = 1;
const pm_slagfriction = 1;
const pm_ladderfriction = 14;
const pm_waterSwimScale = 0.5;
const pm_slagSwimScale = f(0.3);
const pm_proneSpeedScale = f(0.21);

/** ETJump surface flags (re-used monsterslick bits). */
export const SURF_NOJUMPDELAY = 0x10000000;
export const SURF_OVERBOUNCE = 0x40000000;

// pm_flags
export const PMF_DUCKED = 1;
export const PMF_JUMP_HELD = 2;
export const PMF_LADDER = 4;
export const PMF_BACKWARDS_JUMP = 8;
export const PMF_TIME_LAND = 32;
export const PMF_TIME_KNOCKBACK = 64;
export const PMF_TIME_WATERJUMP = 256;
export const PMF_ALL_TIMES = PMF_TIME_WATERJUMP | PMF_TIME_LAND | PMF_TIME_KNOCKBACK;

export const BUTTON_SPRINT = 32;
/** +activate (game logic only: buttons, doors, func_invisible_user; pmove ignores it). */
export const BUTTON_ACTIVATE = 64;
/** usercmd wbuttons */
export const WBUTTON_PRONE = 128;
// entity flags (ps.eFlags)
export const EF_PRONE = 0x00080000;
export const EF_PRONE_MOVING = 0x00100000;
/** ETJump: CONTENTS_DONOTENTER reused as "no prone" volumes */
const CONTENTS_NOPRONE = 0x00200000;

export type PmType = 'normal' | 'noclip';

export interface UserCmd {
  /** Milliseconds; the step length is serverTime - ps.commandTime. */
  serverTime: number;
  /** View angles as 16 bit "shorts" (ANGLE2SHORT). */
  angles: [number, number, number];
  forwardmove: number; // -127..127
  rightmove: number; // -127..127
  upmove: number; // -127..127 (jump > 0, crouch < 0)
  buttons: number;
  /** Extra buttons (WBUTTON_PRONE). */
  wbuttons?: number;
}

export interface PlayerState {
  commandTime: number;
  pmType: PmType;
  pmFlags: number;
  pmTime: number;
  origin: Vec3;
  velocity: Vec3;
  viewangles: Vec3;
  deltaAngles: [number, number, number];
  groundEntityNum: number;
  gravity: number;
  speed: number;
  runSpeedScale: number;
  sprintSpeedScale: number;
  crouchSpeedScale: number;
  mins: Vec3;
  maxs: Vec3;
  crouchMaxZ: number;
  standViewHeight: number;
  crouchViewHeight: number;
  viewheight: number;
  /** EF_PRONE / EF_PRONE_MOVING */
  eFlags: number;
  // pmoveExt_t
  jumpTime: number;
  sprintTime: number;
  /** Time of the last go-prone (positive) or stop-prone (negative) command. */
  proneTime: number;
  /** Height offset of the legs box after a legs step (rendering only). */
  proneLegsOffset: number;
}

export interface MapRules {
  /**
   * 'vanilla' = Wolfenstein: Enemy Territory 2.60b exactly (verified against the original
   * C code by tools/refharness). 'etjump' layers the ETJump mod's movement changes on top:
   * jump delay surface flags, fall damage knockback, overbounce controls, float stamina.
   */
  mod: 'vanilla' | 'etjump';
  /** ETJump worldspawn "nojumpdelay". */
  noJumpDelay: boolean;
  /** ETJump worldspawn "nofalldamage": 0 off, 1 inverts SURF_NODAMAGE, 2 everywhere. */
  noFallDamage: 0 | 1 | 2;
  /** ETJump worldspawn "nooverbounce". */
  noOverbounce: boolean;
  /** ETJump worldspawn "nowallbug". */
  noWallbug: boolean;
  /** Stamina never runs out (sprint and jumps keep it full, like ET's adrenaline powerup). */
  infiniteStamina: boolean;
  /** Server frame length (sv_fps 20 = 50 ms): movers move once per frame. */
  svFrameMsec: number;
}

export const DEFAULT_RULES: MapRules = { mod: 'etjump', noJumpDelay: false, noFallDamage: 0, noOverbounce: false, noWallbug: false, infiniteStamina: true, svFrameMsec: 50 };

export interface PmoveEnv {
  trace(start: Vec3, mins: Vec3, maxs: Vec3, end: Vec3, mask: number): TraceResult;
  pointContents(p: Vec3): number;
}

/** Things that happened during a move, for the game layer (damage, sounds, HUD). */
export interface PmoveEvents {
  jumped: boolean;
  landed: boolean;
  /** Falling damage to apply (9999 = instant death, EV_FALL_NDIE). */
  fallDamage: number;
  /** Upward step height, when a step up occurred. */
  stepped: number;
  /** Entities the move ran into or stood on (pm->touchents, for ClientImpacts). */
  touched: number[];
  // --- animation only (BG_AnimScriptEvent calls and ps fields of ET that do not affect the movement) ---
  /** The jump animation was started (ANIM_ET_JUMP / JUMPBK); force = it interrupts other event animations. */
  jumpAnim: { back: boolean; force: boolean } | null;
  /** Vertical speed before landing, when the move landed (PM_CrashLand plays ANIM_ET_LAND below -220). */
  landVelocity: number | null;
  /** ps->movementDir when PM_SetMovementDir ran (legs yaw relative to the view), else null. */
  movementDir: number | null;
  /** pm->waterlevel */
  waterlevel: number;
  // --- sound only (event parameters of PM_Footsteps, PM_CrashLand and PM_WaterEvents) ---
  /** Length of the move (pml.msec). */
  msec: number;
  /** Water level before the move (pml.previous_waterlevel). */
  previousWaterlevel: number;
  /** Surface flags of the ground trace after the move (0 in the air). */
  groundSurfaceFlags: number;
  /** PM_CrashLand's delta (after the water reduction) when it plays a landing event, else 0. */
  landDelta: number;
  /** Surface flags of the ground the move landed on. */
  landSurfaceFlags: number;
  // --- HUD only (ETJump's PERS_JUMP_SPEED and PmoveUtilsV2 state) ---
  /** Horizontal speed when the move jumped (rounded, PERS_JUMP_SPEED), else 0. */
  jumpSpeed: number;
  /** The walk / air acceleration of the move: wish speed, accel * frametime * wishspeed, speed and angles before it. */
  accel: MoveAccel | null;
}

export interface MoveAccel {
  wishspeed: number;
  /** Speed added at most this move. */
  a: number;
  /** Horizontal speed before the acceleration. */
  speed: number;
  /** Angles (radians) of the velocity and of the wish direction. */
  velAngle: number;
  wishAngle: number;
}

/** Events of a move where nothing happened. */
export function emptyPmoveEvents(): PmoveEvents {
  return {
    jumped: false,
    landed: false,
    fallDamage: 0,
    stepped: 0,
    touched: [],
    jumpAnim: null,
    landVelocity: null,
    movementDir: null,
    waterlevel: 0,
    msec: 0,
    previousWaterlevel: 0,
    groundSurfaceFlags: 0,
    landDelta: 0,
    landSurfaceFlags: 0,
    jumpSpeed: 0,
    accel: null,
  };
}

export function createPlayerState(origin: Vec3, yaw = 0): PlayerState {
  return {
    commandTime: 0,
    pmType: 'normal',
    pmFlags: 0,
    pmTime: 0,
    origin: [f(origin[0]), f(origin[1]), f(origin[2])],
    velocity: [0, 0, 0],
    viewangles: [0, f(yaw), 0],
    deltaAngles: [0, 0, 0],
    groundEntityNum: -1,
    gravity: DEFAULT_GRAVITY,
    speed: DEFAULT_SPEED,
    runSpeedScale: f(0.8),
    sprintSpeedScale: f(1.1),
    crouchSpeedScale: f(0.25),
    mins: copy(PLAYER_MINS),
    maxs: copy(PLAYER_MAXS),
    crouchMaxZ: PLAYER_MAXS[2] - (DEFAULT_VIEWHEIGHT - CROUCH_VIEWHEIGHT),
    standViewHeight: DEFAULT_VIEWHEIGHT,
    crouchViewHeight: CROUCH_VIEWHEIGHT,
    viewheight: DEFAULT_VIEWHEIGHT,
    eFlags: 0,
    jumpTime: -100000,
    sprintTime: SPRINTTIME,
    proneTime: 0,
    proneLegsOffset: 0,
  };
}

const toShort = (x: number): number => (((x + 32768) & 65535) - 32768) | 0;
/** ANGLE2SHORT on a float angle: ( (int)( ( x ) * 65536 / 360 ) & 65535 ) */
const angle2shortF = (x: number): number => Math.trunc(f(f(x * 65536) / 360)) & 65535;
/** q_math.c AngleNormalize360 / AngleNormalize180 */
const angleNormalize360 = (a: number): number => f((360.0 / 65536) * (Math.trunc(a * (65536 / 360.0)) & 65535));
function angleNormalize180(a: number): number {
  a = angleNormalize360(a);
  return a > 180.0 ? f(a - 360.0) : a;
}
/** SHORT2ANGLE( x ) ( ( x ) * ( 360.0 / 65536 ) ), stored as float */
const short2angle = (x: number): number => f(x * (360.0 / 65536));

/** SetClientViewAngle: make the view face `angles` regardless of the cmd angles. */
export function setViewAngles(ps: PlayerState, cmdAngles: [number, number, number], angles: Vec3): void {
  for (let i = 0; i < 3; i++) {
    ps.deltaAngles[i] = toShort(ANGLE2SHORT(angles[i]) - cmdAngles[i]);
    ps.viewangles[i] = f(angles[i]);
  }
}

/** PM_ClipVelocity (overbounce is a float parameter) */
function clipVelocity(inv: Vec3, normal: Vec3, out: Vec3, overbounce: number): void {
  let backoff = fdot(inv, normal);
  if (backoff < 0) backoff = f(backoff * overbounce);
  else backoff = f(backoff / overbounce);
  for (let i = 0; i < 3; i++) {
    const change = f(normal[i] * backoff);
    out[i] = f(inv[i] - change);
  }
}

interface Locals {
  forward: Vec3;
  right: Vec3;
  up: Vec3;
  frametime: number;
  msec: number;
  walking: boolean;
  groundPlane: boolean;
  groundTrace: TraceResult;
  ladder: boolean;
  ladderForward: boolean;
  ladderVec: Vec3;
  impactSpeed: number;
  previousOrigin: Vec3;
  previousVelocity: Vec3;
}

class Pmove {
  ps!: PlayerState;
  cmd!: UserCmd;
  env!: PmoveEnv;
  rules!: MapRules;
  pml!: Locals;
  mins: Vec3 = [0, 0, 0];
  maxs: Vec3 = [0, 0, 0];
  waterlevel = 0;
  watertype = 0;
  tracemask = MASK_PLAYERSOLID;
  events!: PmoveEvents;

  trace(start: Vec3, end: Vec3, mins = this.mins, maxs = this.maxs): TraceResult {
    return this.env.trace(start, mins, maxs, end, this.tracemask);
  }

  get prone(): boolean {
    return (this.ps.eFlags & EF_PRONE) !== 0;
  }

  /**
   * PM_TraceLegs: the legs box 32 units behind a prone player; when the legs
   * clip sooner than the body, try them a step higher.
   */
  traceLegs(start: Vec3, end: Vec3, bodytrace: TraceResult | null, viewangles: Vec3, setOffset: boolean): TraceResult {
    // zinx - don't let players block legs
    const mask = this.tracemask & ~(0x2000000 | 0x4000000);
    if (setOffset) this.ps.proneLegsOffset = 0;
    const angle = f(f(viewangles[1] * M_PI_F) / 180);
    const ofs: Vec3 = [f(f(Math.cos(angle)) * -32), f(f(Math.sin(angle)) * -32), 0];
    const add = (a: Vec3): Vec3 => [f(a[0] + ofs[0]), f(a[1] + ofs[1]), f(a[2] + ofs[2])];
    let trace = this.env.trace(add(start), LEGS_MINS, LEGS_MAXS, add(end), mask);
    if (!bodytrace || trace.fraction < bodytrace.fraction || trace.allsolid) {
      // legs are clipping sooner than body: see if our legs can step up
      ofs[2] = f(ofs[2] + STEPSIZE);
      const org = add(start);
      const point = add(end);
      let steptrace = this.env.trace(org, LEGS_MINS, LEGS_MAXS, point, mask);
      if (!steptrace.allsolid && !steptrace.startsolid && steptrace.fraction > trace.fraction) {
        // the step trace did better -- use it instead
        trace = steptrace;
        if (setOffset) {
          this.ps.proneLegsOffset = ofs[2];
          const o2 = copy(steptrace.endpos);
          const p2: Vec3 = [o2[0], o2[1], f(o2[2] - STEPSIZE)];
          steptrace = this.env.trace(o2, LEGS_MINS, LEGS_MAXS, p2, mask);
          if (!steptrace.allsolid) this.ps.proneLegsOffset = f(ofs[2] - f(o2[2] - steptrace.endpos[2]));
        }
      }
    }
    return trace;
  }

  /** PM_TraceAllLegs: body trace, plus the legs while prone. */
  traceAll(start: Vec3, end: Vec3, setLegsOffset = false): TraceResult {
    const trace = this.trace(start, end);
    if (!this.prone) return trace;
    const legs = this.traceLegs(start, end, trace, this.ps.viewangles, setLegsOffset);
    if (legs.fraction < trace.fraction || legs.startsolid || legs.allsolid) {
      // the legs trace started behind the player: recompute the end position along the body move
      for (let i = 0; i < 3; i++) legs.endpos[i] = f(start[i] + f(legs.fraction * f(end[i] - start[i])));
      return legs;
    }
    return trace;
  }

  run(ps: PlayerState, cmdIn: UserCmd, env: PmoveEnv, rules: MapRules): PmoveEvents {
    this.ps = ps;
    this.cmd = { ...cmdIn, angles: [cmdIn.angles[0], cmdIn.angles[1], cmdIn.angles[2]] };
    this.env = env;
    this.rules = rules;
    this.events = emptyPmoveEvents();
    this.waterlevel = 0;
    this.watertype = 0;

    let msec = this.cmd.serverTime - ps.commandTime;
    if (msec < 1) msec = 1;
    else if (msec > 200) msec = 200;
    ps.commandTime = this.cmd.serverTime;

    this.pml = {
      forward: [0, 0, 0],
      right: [0, 0, 0],
      up: [0, 0, 0],
      frametime: f(msec * 0.001),
      msec,
      walking: false,
      groundPlane: false,
      groundTrace: emptyGroundTrace(),
      ladder: false,
      ladderForward: false,
      ladderVec: [0, 0, 0],
      impactSpeed: 0,
      previousOrigin: copy(ps.origin),
      previousVelocity: copy(ps.velocity),
    };
    this.events.msec = msec;

    this.updateViewAngles();
    fangleVectors(ps.viewangles, this.pml.forward, this.pml.right, this.pml.up);

    if (this.cmd.upmove < 10) ps.pmFlags &= ~PMF_JUMP_HELD; // not holding jump
    if (rules.mod === 'etjump') {
      // ETJump normalizes the jump input and removes the -128 asymmetry
      if (this.cmd.upmove > 0) this.cmd.upmove = 127;
      if (this.cmd.rightmove < -127) this.cmd.rightmove = -127;
      if (this.cmd.forwardmove < -127) this.cmd.forwardmove = -127;
    }

    if (ps.pmType === 'noclip') {
      this.noclipMove();
      this.dropTimers();
      return this.events;
    }

    this.setWaterLevel();
    this.events.previousWaterlevel = this.waterlevel;
    if (!this.checkProne()) this.checkDuck();
    this.groundTrace();
    this.checkLadderMove();
    this.dropTimers();

    if (this.pml.ladder) this.ladderMove();
    else if (ps.pmFlags & PMF_TIME_WATERJUMP) this.waterJumpMove();
    else if (this.waterlevel > 1) this.waterMove();
    else if (this.pml.walking) this.walkMove();
    else this.airMove();

    this.sprint();
    this.groundTrace();
    this.setWaterLevel();

    // snap some parts of playerstate to save network bandwidth (trap_SnapVector: round to nearest even)
    ps.velocity[0] = rintf(ps.velocity[0]);
    ps.velocity[1] = rintf(ps.velocity[1]);
    ps.velocity[2] = rintf(ps.velocity[2]);
    this.events.waterlevel = this.waterlevel;
    if (ps.groundEntityNum !== ENTITYNUM_NONE) this.events.groundSurfaceFlags = this.pml.groundTrace.surfaceFlags;
    return this.events;
  }

  private updateViewAngles(): void {
    const { ps, cmd } = this;
    const oldYaw = ps.viewangles[1];
    // ETJump never takes the roll from the command
    const count = this.rules.mod === 'etjump' ? 2 : 3;
    for (let i = 0; i < count; i++) {
      let temp = toShort(cmd.angles[i] + ps.deltaAngles[i]);
      if (i === 0) {
        // don't let the player look up or down more than 90 degrees
        if (temp > 16000) {
          ps.deltaAngles[i] = toShort(16000 - cmd.angles[i]);
          temp = 16000;
        } else if (temp < -16000) {
          ps.deltaAngles[i] = toShort(-16000 - cmd.angles[i]);
          temp = -16000;
        }
      }
      ps.viewangles[i] = short2angle(temp);
    }

    if (this.prone) {
      // prone: pitch limited around the (unused, zero) mounted weapon angles,
      // and the view may not turn the legs into a wall
      const newDeltaAngle = ps.deltaAngles[1];
      const pitchMax = 40;
      let pitchDiff = ps.viewangles[0]; // - mountedWeaponAngles[PITCH] (0)
      if (pitchDiff > 180) pitchDiff = f(pitchDiff - 360);
      else if (pitchDiff < -180) pitchDiff = f(pitchDiff + 360);
      if (pitchDiff > pitchMax) {
        ps.viewangles[0] = angleNormalize180(pitchMax);
        ps.deltaAngles[0] = angle2shortF(ps.viewangles[0]) - cmd.angles[0];
      } else if (pitchDiff < -pitchMax) {
        ps.viewangles[0] = angleNormalize180(-pitchMax);
        ps.deltaAngles[0] = angle2shortF(ps.viewangles[0]) - cmd.angles[0];
      }
      if (ps.viewangles[1] !== oldYaw) {
        const legs = this.traceLegs(ps.origin, ps.origin, null, ps.viewangles, true);
        if (legs.allsolid) {
          ps.viewangles[1] = oldYaw;
          ps.deltaAngles[1] = angle2shortF(ps.viewangles[1]) - cmd.angles[1];
        } else {
          ps.deltaAngles[1] = newDeltaAngle;
        }
      }
    }
  }

  private cmdScale(cmd: UserCmd): number {
    const { ps } = this;
    let max = Math.abs(cmd.forwardmove);
    if (Math.abs(cmd.rightmove) > max) max = Math.abs(cmd.rightmove);
    if (Math.abs(cmd.upmove) > max) max = Math.abs(cmd.upmove);
    if (!max) return 0;
    const total = f(Math.sqrt(cmd.forwardmove * cmd.forwardmove + cmd.rightmove * cmd.rightmove + cmd.upmove * cmd.upmove));
    // (float)speed * max / ( 127.0 * total ): float product, double division
    let s = f(f(ps.speed * max) / (127.0 * total));
    if (this.cmd.buttons & BUTTON_SPRINT && ps.sprintTime > 50) s = f(s * ps.sprintSpeedScale);
    else s = f(s * ps.runSpeedScale);
    if (ps.pmType === 'noclip') s = f(s * 3);
    return s;
  }

  /** The acceleration a move is about to apply, for the HUD (plain doubles, nothing feeds back). */
  private recordAccel(wishdir: Vec3, wishspeed: number, accel: number): void {
    const v = this.ps.velocity;
    this.events.accel = { wishspeed, a: accel * this.pml.frametime * wishspeed, speed: Math.hypot(v[0], v[1]), velAngle: Math.atan2(v[1], v[0]), wishAngle: Math.atan2(wishdir[1], wishdir[0]) };
  }

  private accelerate(wishdir: Vec3, wishspeed: number, accel: number): void {
    const { ps } = this;
    const currentspeed = fdot(ps.velocity, wishdir);
    const addspeed = f(wishspeed - currentspeed);
    if (addspeed <= 0) return;
    let accelspeed = f(f(accel * this.pml.frametime) * wishspeed);
    if (accelspeed > addspeed) accelspeed = addspeed;
    // (accelspeed *= 1.0 / ps->friction is a no-op: friction is always 1.0 for players)
    for (let i = 0; i < 3; i++) ps.velocity[i] = f(ps.velocity[i] + f(accelspeed * wishdir[i]));
  }

  private friction(): void {
    const { ps, pml } = this;
    const vel = ps.velocity;
    const vec: Vec3 = copy(vel);
    if (pml.walking) vec[2] = 0; // ignore slope movement
    const speed = flength(vec);
    if (speed < 1 && ps.pmType !== 'noclip') {
      vel[0] = 0;
      vel[1] = 0; // allow sinking underwater
      return;
    }
    let drop = 0;
    // apply ground friction
    if (this.waterlevel <= 1) {
      if (pml.walking && !(pml.groundTrace.surfaceFlags & SURF_SLICK)) {
        // if getting knocked back, no friction
        if (!(ps.pmFlags & PMF_TIME_KNOCKBACK)) {
          const control = speed < pm_stopspeed ? pm_stopspeed : speed;
          drop = f(drop + f(f(control * pm_friction) * pml.frametime));
        }
      }
    }
    // apply water friction even if just wading
    if (this.waterlevel) {
      const fr = this.watertype === CONTENTS_SLIME ? pm_slagfriction : pm_waterfriction;
      drop = f(drop + f(f(f(speed * fr) * this.waterlevel) * pml.frametime));
    }
    // apply ladder strafe friction
    if (pml.ladder) drop = f(drop + f(f(speed * pm_ladderfriction) * pml.frametime));

    let newspeed = f(speed - drop);
    if (newspeed < 0) newspeed = 0;
    newspeed = f(newspeed / speed);
    if (ps.pmType === 'noclip' && drop < 1 && speed < 3) newspeed = 0;
    fscale(vel, vel, newspeed);
  }

  private checkJump(): boolean {
    const { ps, cmd, pml, rules } = this;
    // no jumpin when prone
    if (this.prone) return false;
    if (cmd.serverTime - ps.jumpTime < JUMP_DELAY_TIME) {
      if (rules.mod === 'vanilla') return false;
      // ETJump: never right after standing up from prone (hasJustStoodUp)
      if (ps.proneTime < 0 && -ps.proneTime - ps.jumpTime === 650) return false;
      // ETJump: SURF_NOJUMPDELAY marks surfaces where the delay is inverted
      const flagged = (pml.groundTrace.surfaceFlags & SURF_NOJUMPDELAY) !== 0;
      if (rules.noJumpDelay ? flagged : !flagged) return false;
    }
    if (cmd.upmove < 10) return false; // not holding jump
    // must wait for jump to be released
    if (ps.pmFlags & PMF_JUMP_HELD) {
      // clear upmove so cmdscale doesn't lower running speed
      cmd.upmove = 0;
      return false;
    }
    pml.groundPlane = false; // jumping away
    pml.walking = false;
    ps.pmFlags |= PMF_JUMP_HELD;
    ps.groundEntityNum = ENTITYNUM_NONE;
    ps.velocity[2] = JUMP_VELOCITY;
    this.setJumpDirection(true);
    this.events.jumped = true;
    this.events.jumpSpeed = Math.round(Math.hypot(ps.velocity[0], ps.velocity[1]));
    return true;
  }

  /** PMF_BACKWARDS_JUMP selects the jump animation (forward / backwards), started here too. */
  private setJumpDirection(force: boolean): void {
    if (this.cmd.forwardmove >= 0) this.ps.pmFlags &= ~PMF_BACKWARDS_JUMP;
    else this.ps.pmFlags |= PMF_BACKWARDS_JUMP;
    this.events.jumpAnim = { back: this.cmd.forwardmove < 0, force };
  }

  /** PM_SetMovementDir: legs yaw relative to the view, from the actual move (animation only, plain doubles). */
  private setMovementDir(): void {
    const { ps, pml, cmd } = this;
    const dx = ps.origin[0] - pml.previousOrigin[0];
    const dy = ps.origin[1] - pml.previousOrigin[1];
    const dz = ps.origin[2] - pml.previousOrigin[2];
    const speed = Math.hypot(dx, dy, dz);
    // slower than 20 units per second: just face the head angles
    if ((cmd.forwardmove || cmd.rightmove) && ps.groundEntityNum !== ENTITYNUM_NONE && speed && speed > pml.frametime * 5) {
      const yaw = (Math.atan2(dy, dx) * 180) / Math.PI;
      const norm180 = (a: number) => {
        const n = (360 / 65536) * (Math.trunc(a * (65536 / 360)) & 65535);
        return n > 180 ? n - 360 : n;
      };
      let moveyaw = Math.trunc(norm180(yaw - ps.viewangles[1]));
      if (cmd.forwardmove < 0) moveyaw = Math.trunc(norm180(moveyaw + 180));
      this.events.movementDir = Math.max(-75, Math.min(75, moveyaw));
    } else this.events.movementDir = 0;
  }

  private walkMove(): void {
    const { ps, pml } = this;
    if (this.waterlevel > 2 && fdot(pml.forward, pml.groundTrace.plane.normal) > 0) {
      // begin swimming
      this.waterMove();
      return;
    }

    if (this.checkJump()) {
      // jumped away
      if (this.waterlevel > 1) this.waterMove();
      else this.airMove();
      if (!(this.cmd.serverTime - ps.jumpTime < JUMP_DELAY_TIME)) {
        ps.sprintTime -= 2500;
        if (ps.sprintTime < 0) ps.sprintTime = 0;
        ps.jumpTime = this.cmd.serverTime;
      } else if (this.rules.mod === 'etjump') {
        ps.jumpTime = this.cmd.serverTime; // ETJump refreshes the jump time on every jump
      }
      return;
    }

    this.friction();
    const fmove = this.cmd.forwardmove;
    const smove = this.cmd.rightmove;
    const s = this.cmdScale(this.cmd);

    // project moves down to flat plane
    pml.forward[2] = 0;
    pml.right[2] = 0;
    // project the forward and right directions onto the ground plane
    clipVelocity(pml.forward, pml.groundTrace.plane.normal, pml.forward, OVERCLIP_F);
    clipVelocity(pml.right, pml.groundTrace.plane.normal, pml.right, OVERCLIP_F);
    fnormalize(pml.forward);
    fnormalize(pml.right);

    const wishdir: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) wishdir[i] = f(f(pml.forward[i] * fmove) + f(pml.right[i] * smove));
    let wishspeed = fnormalize(wishdir);
    wishspeed = f(wishspeed * s);

    // clamp the speed lower if prone / ducking
    if (this.prone) {
      const max = f(ps.speed * pm_proneSpeedScale);
      if (wishspeed > max) wishspeed = max;
    } else if (ps.pmFlags & PMF_DUCKED) {
      const max = f(ps.speed * ps.crouchSpeedScale);
      if (wishspeed > max) wishspeed = max;
    }
    // clamp the speed lower if wading or walking on the bottom
    if (this.waterlevel) {
      let waterScale = f(this.waterlevel / 3.0);
      const swim = this.watertype === CONTENTS_SLIME ? pm_slagSwimScale : pm_waterSwimScale;
      waterScale = f(1.0 - (1.0 - swim) * waterScale);
      const max = f(ps.speed * waterScale);
      if (wishspeed > max) wishspeed = max;
    }

    // when a player gets hit, they temporarily lose full control
    const slick = (pml.groundTrace.surfaceFlags & SURF_SLICK) !== 0 || (ps.pmFlags & PMF_TIME_KNOCKBACK) !== 0;
    this.recordAccel(wishdir, wishspeed, slick ? pm_airaccelerate : pm_accelerate);
    this.accelerate(wishdir, wishspeed, slick ? pm_airaccelerate : pm_accelerate);
    if (slick) ps.velocity[2] = f(ps.velocity[2] - f(ps.gravity * pml.frametime));

    const vel = flength(ps.velocity);
    // slide along the ground plane
    clipVelocity(ps.velocity, pml.groundTrace.plane.normal, ps.velocity, OVERCLIP_F);
    // don't do anything if standing still
    if (!ps.velocity[0] && !ps.velocity[1]) return;
    // don't decrease velocity when going up or down a slope
    fnormalize(ps.velocity);
    fscale(ps.velocity, ps.velocity, vel);

    this.stepSlideMove(false);
    this.setMovementDir();
  }

  private airMove(): void {
    const { ps, pml } = this;
    this.friction();
    const fmove = this.cmd.forwardmove;
    const smove = this.cmd.rightmove;
    const s = this.cmdScale(this.cmd);

    // project moves down to flat plane
    pml.forward[2] = 0;
    pml.right[2] = 0;
    fnormalize(pml.forward);
    fnormalize(pml.right);

    const wishdir: Vec3 = [f(f(pml.forward[0] * fmove) + f(pml.right[0] * smove)), f(f(pml.forward[1] * fmove) + f(pml.right[1] * smove)), 0];
    let wishspeed = fnormalize(wishdir);
    wishspeed = f(wishspeed * s);

    // not on ground, so little effect on velocity
    this.recordAccel(wishdir, wishspeed, pm_airaccelerate);
    this.accelerate(wishdir, wishspeed, pm_airaccelerate);

    // we may have a ground plane that is very steep, even though we don't
    // have a groundentity: slide along the steep plane
    if (pml.groundPlane) clipVelocity(ps.velocity, pml.groundTrace.plane.normal, ps.velocity, OVERCLIP_F);

    this.stepSlideMove(true);
    this.setMovementDir();
  }

  private waterMove(): void {
    const { ps, pml } = this;
    if (this.checkWaterJump()) {
      this.waterJumpMove();
      return;
    }
    this.friction();
    const s = this.cmdScale(this.cmd);
    const wishvel: Vec3 = [0, 0, 0];
    if (!s) {
      wishvel[2] = -60; // sink towards bottom
    } else {
      for (let i = 0; i < 3; i++) wishvel[i] = f(f(f(s * pml.forward[i]) * this.cmd.forwardmove) + f(f(s * pml.right[i]) * this.cmd.rightmove));
      wishvel[2] = f(wishvel[2] + f(s * this.cmd.upmove));
    }
    const wishdir = copy(wishvel);
    let wishspeed = fnormalize(wishdir);
    const slime = this.watertype === CONTENTS_SLIME;
    const max = f(ps.speed * (slime ? pm_slagSwimScale : pm_waterSwimScale));
    if (wishspeed > max) wishspeed = max;
    this.accelerate(wishdir, wishspeed, slime ? pm_slagaccelerate : pm_wateraccelerate);

    // make sure we can go up slopes easily under water
    if (pml.groundPlane && fdot(ps.velocity, pml.groundTrace.plane.normal) < 0) {
      const vel = flength(ps.velocity);
      clipVelocity(ps.velocity, pml.groundTrace.plane.normal, ps.velocity, OVERCLIP_F);
      fnormalize(ps.velocity);
      fscale(ps.velocity, ps.velocity, vel);
    }
    this.slideMove(false);
  }

  private checkWaterJump(): boolean {
    const { ps, pml } = this;
    if (ps.pmTime) return false;
    if (this.waterlevel !== 2) return false;
    const flatforward: Vec3 = [pml.forward[0], pml.forward[1], 0];
    fnormalize(flatforward);
    const spot = fma3([0, 0, 0], ps.origin, 30, flatforward);
    spot[2] = f(spot[2] + 4);
    if (!(this.env.pointContents(spot) & CONTENTS_SOLID)) return false;
    spot[2] = f(spot[2] + 16);
    if (this.env.pointContents(spot)) return false;
    // jump out of water
    fscale(ps.velocity, pml.forward, 200);
    ps.velocity[2] = 350;
    ps.pmFlags |= PMF_TIME_WATERJUMP;
    ps.pmTime = 2000;
    return true;
  }

  private waterJumpMove(): void {
    const { ps } = this;
    // waterjump has no control, but falls
    this.stepSlideMove(true);
    ps.velocity[2] = f(ps.velocity[2] - f(ps.gravity * this.pml.frametime));
    if (ps.velocity[2] < 0) {
      // cancel as soon as we are falling down again
      ps.pmFlags &= ~PMF_ALL_TIMES;
      ps.pmTime = 0;
    }
  }

  private noclipMove(): void {
    const { ps, pml } = this;
    ps.viewheight = DEFAULT_VIEWHEIGHT;
    const speed = flength(ps.velocity);
    if (speed < 1) {
      ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
    } else {
      const friction = f(pm_friction * 1.5); // extra friction
      const control = speed < pm_stopspeed ? pm_stopspeed : speed;
      const drop = f(f(control * friction) * pml.frametime);
      let newspeed = f(speed - drop);
      if (newspeed < 0) newspeed = 0;
      newspeed = f(newspeed / speed);
      fscale(ps.velocity, ps.velocity, newspeed);
    }
    const s = this.cmdScale(this.cmd);
    const fmove = this.cmd.forwardmove;
    const smove = this.cmd.rightmove;
    const wishvel: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) wishvel[i] = f(f(pml.forward[i] * fmove) + f(pml.right[i] * smove));
    wishvel[2] = f(wishvel[2] + this.cmd.upmove);
    const wishdir = copy(wishvel);
    const wishspeed = f(fnormalize(wishdir) * s);
    this.accelerate(wishdir, wishspeed, pm_accelerate);
    fma3(ps.origin, ps.origin, pml.frametime, ps.velocity);
  }

  private ladderMove(): void {
    const { ps, pml } = this;
    if (pml.ladderForward) {
      // move towards the ladder: VectorScale( laddervec, -200.0, wishvel ) (double factor)
      ps.velocity[0] = f(pml.ladderVec[0] * -200.0);
      ps.velocity[1] = f(pml.ladderVec[1] * -200.0);
    }
    let upscale = f((pml.forward[2] + 0.5) * 2.5);
    if (upscale > 1.0) upscale = 1;
    else if (upscale < -1.0) upscale = -1;

    // forward/right should be horizontal only
    pml.forward[2] = 0;
    pml.right[2] = 0;
    fnormalize(pml.forward);
    fnormalize(pml.right);

    const s = this.cmdScale(this.cmd);
    const wishvel: Vec3 = [0, 0, 0];
    if (this.cmd.forwardmove) wishvel[2] = f(0.9 * upscale * s * this.cmd.forwardmove);
    if (this.cmd.rightmove) {
      // strafe, so we can jump off ladder
      const ang = fvectoangles(pml.ladderVec, [0, 0, 0]);
      const ladderRight: Vec3 = [0, 0, 0];
      fangleVectors(ang, null, ladderRight, null);
      // if we are looking away from the ladder, reverse the right vector
      if (fdot(pml.ladderVec, pml.forward) < 0) {
        ladderRight[0] = -ladderRight[0];
        ladderRight[1] = -ladderRight[1];
        ladderRight[2] = -ladderRight[2];
      }
      const k = 0.5 * s * this.cmd.rightmove; // double
      for (let i = 0; i < 3; i++) wishvel[i] = f(wishvel[i] + ladderRight[i] * k);
    }

    // do strafe friction
    this.friction();
    if (ps.velocity[0] < 1 && ps.velocity[0] > -1) ps.velocity[0] = 0;
    if (ps.velocity[1] < 1 && ps.velocity[1] > -1) ps.velocity[1] = 0;

    const wishdir: Vec3 = [0, 0, 0];
    const wishspeed = fnormalize2(wishvel, wishdir);
    this.accelerate(wishdir, wishspeed, pm_accelerate);
    if (!wishvel[2]) {
      const g = f(ps.gravity * pml.frametime);
      if (ps.velocity[2] > 0) {
        ps.velocity[2] = f(ps.velocity[2] - g);
        if (ps.velocity[2] < 0) ps.velocity[2] = 0;
      } else {
        ps.velocity[2] = f(ps.velocity[2] + g);
        if (ps.velocity[2] > 0) ps.velocity[2] = 0;
      }
    }
    this.stepSlideMove(false); // no gravity while going up ladder
  }

  private checkLadderMove(): void {
    const { ps, pml } = this;
    if (ps.pmTime) return;
    const tracedist = pml.walking ? 1.0 : TRACE_LADDER_DIST;
    pml.ladder = false;
    ps.pmFlags &= ~PMF_LADDER;
    pml.ladderForward = false;

    // can't climb ladders while prone
    if (this.prone) return;

    // check for ladder
    const flatforward: Vec3 = [pml.forward[0], pml.forward[1], 0];
    fnormalize(flatforward);
    let spot = fma3([0, 0, 0], ps.origin, tracedist, flatforward);
    let trace = this.trace(ps.origin, spot);
    if (trace.fraction < 1 && trace.surfaceFlags & SURF_LADDER) pml.ladder = true;
    if (pml.ladder) pml.ladderVec = copy(trace.plane.normal);

    if (pml.ladder && !pml.walking && f(trace.fraction * tracedist) > 1.0) {
      // if we are only just on the ladder, don't do this yet, or it may throw us back off the ladder
      pml.ladder = false;
      const mins = copy(this.mins);
      mins[2] = -1;
      spot = fma3([0, 0, 0], ps.origin, -tracedist, pml.ladderVec);
      trace = this.trace(ps.origin, spot, mins, this.maxs);
      if (trace.fraction < 1 && trace.surfaceFlags & SURF_LADDER) {
        pml.ladderForward = true;
        pml.ladder = true;
        ps.pmFlags |= PMF_LADDER;
      } else {
        pml.ladder = false;
      }
    } else if (pml.ladder) {
      ps.pmFlags |= PMF_LADDER;
    }

    // create some up/down velocity if touching ladder
    if (pml.ladder && pml.walking) {
      // we are currently on the ground, only go up and prevent X/Y if we are pushing forwards
      if (this.cmd.forwardmove <= 0) pml.ladder = false;
    }
  }

  /** PM_CheckProne: returns true while prone (mins/maxs/viewheight set here). */
  private checkProne(): boolean {
    const { ps, cmd } = this;
    const crouchBox = () => {
      this.mins = copy(ps.mins);
      this.maxs = [ps.maxs[0], ps.maxs[1], ps.crouchMaxZ];
    };
    // ETJump: "noprone" volumes (CONTENTS_DONOTENTER) force players up
    const pronable = () => {
      if (this.rules.mod !== 'etjump') return true;
      return !this.env.trace(ps.origin, ps.mins, ps.maxs, ps.origin, CONTENTS_NOPRONE).startsolid;
    };
    const wantsProne = ((cmd.wbuttons ?? 0) & WBUTTON_PRONE) !== 0;
    if (!pronable()) {
      ps.eFlags &= ~(EF_PRONE | EF_PRONE_MOVING);
      return false;
    }

    if (!this.prone) {
      // can't go prone on ladders or while swimming (double tap forward is not supported)
      if (!(ps.pmFlags & PMF_LADDER) && this.waterlevel <= 1 && wantsProne && cmd.serverTime - -ps.proneTime > 750) {
        crouchBox();
        ps.eFlags |= EF_PRONE;
        const trace = this.traceAll(ps.origin, ps.origin);
        ps.eFlags &= ~EF_PRONE;
        if (!trace.startsolid && !trace.allsolid) {
          // go prone
          ps.pmFlags |= PMF_DUCKED; // crouched as well
          ps.eFlags |= EF_PRONE;
          ps.proneTime = cmd.serverTime; // timestamp 'go prone'
        }
      }
    }

    if (this.prone) {
      if (this.waterlevel > 1 || ((cmd.upmove > 10 || wantsProne) && cmd.serverTime - ps.proneTime > 750)) {
        // see if we have the space to stop prone
        crouchBox();
        ps.eFlags &= ~EF_PRONE;
        const trace = this.traceAll(ps.origin, ps.origin);
        ps.eFlags |= EF_PRONE;
        if (!trace.allsolid) {
          // crouch for a bit, stop prone
          ps.pmFlags |= PMF_DUCKED;
          ps.eFlags &= ~(EF_PRONE | EF_PRONE_MOVING);
          ps.proneTime = -cmd.serverTime; // timestamp 'stop prone'
          // don't jump for a bit
          ps.jumpTime = cmd.serverTime - 650;
        }
      }
    }

    if (!this.prone) return false;
    // see if we are moving
    const spd = flength(ps.velocity);
    const userinput = Math.abs(cmd.forwardmove) + Math.abs(cmd.rightmove) > 10;
    if (userinput && spd > 40 && !(ps.eFlags & EF_PRONE_MOVING)) ps.eFlags |= EF_PRONE_MOVING;
    else if (!userinput && spd < 20 && ps.eFlags & EF_PRONE_MOVING) ps.eFlags &= ~EF_PRONE_MOVING;
    this.mins = copy(ps.mins);
    this.maxs = [ps.maxs[0], ps.maxs[1], ps.maxs[2] - ps.standViewHeight - PRONE_VIEWHEIGHT];
    ps.viewheight = PRONE_VIEWHEIGHT;
    return true;
  }

  private checkDuck(): void {
    const { ps } = this;
    this.mins = copy(ps.mins);
    this.maxs = [ps.maxs[0], ps.maxs[1], ps.maxs[2]];
    if (this.cmd.upmove < 0 && !(ps.pmFlags & PMF_LADDER)) {
      ps.pmFlags |= PMF_DUCKED; // duck
    } else if (ps.pmFlags & PMF_DUCKED) {
      // try to stand up
      this.maxs[2] = ps.maxs[2];
      const trace = this.traceAll(ps.origin, ps.origin);
      if (!trace.allsolid) ps.pmFlags &= ~PMF_DUCKED;
    }
    if (ps.pmFlags & PMF_DUCKED) {
      this.maxs[2] = ps.crouchMaxZ;
      ps.viewheight = ps.crouchViewHeight;
    } else {
      this.maxs[2] = ps.maxs[2];
      ps.viewheight = ps.standViewHeight;
    }
  }

  private setWaterLevel(): void {
    const { ps } = this;
    this.waterlevel = 0;
    this.watertype = 0;
    const base = f(ps.origin[2] + ps.mins[2]);
    const point: Vec3 = [ps.origin[0], ps.origin[1], f(base + 1)];
    let cont = this.env.pointContents(point);
    if (cont & MASK_WATER) {
      const sample2 = Math.trunc(ps.viewheight - ps.mins[2]);
      const sample1 = Math.trunc(sample2 / 2);
      this.watertype = cont;
      this.waterlevel = 1;
      point[2] = f(base + sample1);
      cont = this.env.pointContents(point);
      if (cont & MASK_WATER) {
        this.waterlevel = 2;
        point[2] = f(base + sample2);
        cont = this.env.pointContents(point);
        if (cont & MASK_WATER) this.waterlevel = 3;
      }
    }
  }

  private dropTimers(): void {
    const { ps, pml } = this;
    if (ps.pmTime) {
      if (pml.msec >= ps.pmTime) {
        ps.pmFlags &= ~PMF_ALL_TIMES;
        ps.pmTime = 0;
      } else {
        ps.pmTime -= pml.msec;
      }
    }
  }

  private sprint(): void {
    const { ps, cmd, pml } = this;
    if (this.rules.infiniteStamina) {
      // PW_ADRENALINE: the sprint bar is refilled every frame
      ps.sprintTime = SPRINTTIME;
      return;
    }
    // vanilla stores sprintTime in an int (float maths, then truncated); ETJump uses a float
    const store = this.rules.mod === 'vanilla' ? (v: number) => Math.trunc(f(v)) : f;
    if (cmd.buttons & BUTTON_SPRINT && (cmd.forwardmove || cmd.rightmove) && !(ps.pmFlags & PMF_DUCKED) && !this.prone) {
      ps.sprintTime = store(ps.sprintTime - f(5000 * pml.frametime));
      if (ps.sprintTime < 0) ps.sprintTime = 0;
    } else {
      // recharge faster for the top 75% of the sprint bar
      const recharge = f(500 * pml.frametime);
      ps.sprintTime = store(ps.sprintTime + recharge);
      if (ps.sprintTime > 5000) ps.sprintTime = store(ps.sprintTime + recharge);
      if (ps.sprintTime > SPRINTTIME) ps.sprintTime = SPRINTTIME;
    }
  }

  private surfaceAllowsOverbounce(trace: TraceResult): boolean {
    const flagged = (trace.surfaceFlags & SURF_OVERBOUNCE) !== 0;
    return this.rules.noOverbounce ? flagged : !flagged;
  }

  private groundTrace(): void {
    const { ps, pml } = this;
    const point: Vec3 = [ps.origin[0], ps.origin[1], f(ps.origin[2] - 0.25)];
    let trace = this.traceAll(ps.origin, point, true);
    pml.groundTrace = trace;

    if (trace.allsolid && this.rules.mod === 'etjump' && this.rules.noWallbug) ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;

    // do something corrective if the trace starts in a solid...
    if (trace.allsolid) {
      const fixed = this.correctAllSolid();
      if (!fixed) return;
      trace = fixed;
    }

    // if the trace didn't hit anything, we are in free fall
    if (trace.fraction === 1.0) {
      this.groundTraceMissed();
      pml.groundPlane = false;
      pml.walking = false;
      return;
    }

    // check if getting thrown off the ground
    // (ETJump throws prone players off the ground too; vanilla keeps them on it)
    if (ps.velocity[2] > 0 && fdot(ps.velocity, trace.plane.normal) > 10 && (this.rules.mod === 'etjump' || !this.prone)) {
      this.setJumpDirection(false); // go into jump animation
      ps.groundEntityNum = ENTITYNUM_NONE;
      pml.groundPlane = false;
      pml.walking = false;
      return;
    }

    // slopes that are too steep will not be considered onground
    if (trace.plane.normal[2] < MIN_WALK_NORMAL) {
      ps.groundEntityNum = ENTITYNUM_NONE;
      pml.groundPlane = true;
      pml.walking = false;
      return;
    }

    pml.groundPlane = true;
    pml.walking = true;

    // hitting solid ground will end a waterjump
    if (ps.pmFlags & PMF_TIME_WATERJUMP) {
      ps.pmFlags &= ~(PMF_TIME_WATERJUMP | PMF_TIME_LAND);
      ps.pmTime = 0;
    }

    if (ps.groundEntityNum === ENTITYNUM_NONE) {
      // just hit the ground
      this.crashLand();
      const etjump = this.rules.mod === 'etjump';
      if (etjump && !this.surfaceAllowsOverbounce(trace)) clipVelocity(ps.velocity, trace.plane.normal, ps.velocity, OVERCLIP_F);
      // don't do landing time if we were just going down a slope
      if (pml.previousVelocity[2] < -200) {
        // don't allow another jump for a little while
        ps.pmFlags |= PMF_TIME_LAND;
        // ETJump (like ET:Legacy) does not shorten a longer knockback timer
        if (!etjump || ps.pmTime < 250) ps.pmTime = 250;
      }
    }

    ps.groundEntityNum = trace.entityNum;
    this.addTouchEnt(trace.entityNum);

    // ETJump: prevent sticky overbounces on surfaces that disallow them
    if (this.rules.mod === 'etjump' && !this.surfaceAllowsOverbounce(trace) && trace.plane.normal[2] === 1) {
      ps.velocity[2] = 0;
      if (this.waterlevel < 2) ps.origin[2] = f(ps.origin[2] - f(ps.origin[2] - trace.endpos[2]));
    }
  }

  /** PM_AddTouchEnt */
  private addTouchEnt(entityNum: number): void {
    if (entityNum === ENTITYNUM_WORLD || entityNum === ENTITYNUM_NONE) return;
    if (!this.events.touched.includes(entityNum)) this.events.touched.push(entityNum);
  }

  private correctAllSolid(): TraceResult | null {
    const { ps, pml } = this;
    // jitter around
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        for (let k = -1; k <= 1; k++) {
          const point: Vec3 = [f(ps.origin[0] + i), f(ps.origin[1] + j), f(ps.origin[2] + k)];
          let trace = this.traceAll(point, point);
          if (!trace.allsolid) {
            trace = this.traceAll(ps.origin, [ps.origin[0], ps.origin[1], f(ps.origin[2] - 0.25)]);
            pml.groundTrace = trace;
            return trace;
          }
        }
      }
    }
    ps.groundEntityNum = ENTITYNUM_NONE;
    pml.groundPlane = false;
    pml.walking = false;
    return null;
  }

  private groundTraceMissed(): void {
    const { ps, pml } = this;
    if (ps.groundEntityNum !== ENTITYNUM_NONE) {
      // we just transitioned into freefall: if the ground is a ways away, force the jump animation
      const trace = this.traceAll(ps.origin, [ps.origin[0], ps.origin[1], f(ps.origin[2] - 64)]);
      if (trace.fraction === 1.0) this.setJumpDirection(true);
    }
    // If we've never yet touched the ground, it's because we're spawning
    if (ps.groundEntityNum !== -1) ps.groundEntityNum = ENTITYNUM_NONE;
    pml.groundPlane = false;
    pml.walking = false;
  }

  private crashLand(): void {
    const { ps, pml, rules } = this;
    this.events.landed = true;
    this.events.landVelocity = pml.previousVelocity[2];
    // calculate the exact velocity on landing
    const dist = f(ps.origin[2] - pml.previousOrigin[2]);
    const vel = pml.previousVelocity[2];
    const acc = f(-ps.gravity);
    const a = f(acc / 2);
    const b = vel;
    const c = -dist;
    const den = f(f(b * b) - f(f(4 * a) * c));
    if (den < 0 || a === 0) return;
    const t = f((-b - Math.sqrt(den)) / f(2 * a));
    let delta = f(vel + f(t * acc));
    delta = f(f(delta * delta) * 0.0001);

    // never take falling damage if completely underwater
    if (this.waterlevel === 3) return;
    // reduce falling damage if there is standing water
    if (this.waterlevel === 2) delta = f(delta * 0.25);
    if (this.waterlevel === 1) delta = f(delta * 0.5);
    if (delta < 1) return;
    this.events.landDelta = delta;
    this.events.landSurfaceFlags = pml.groundTrace.surfaceFlags;

    const noDamageSurface = (pml.groundTrace.surfaceFlags & SURF_NODAMAGE) !== 0;
    if (rules.mod === 'vanilla') {
      if (!noDamageSurface) {
        if (delta > 77) this.events.fallDamage = 9999;
        else if (delta > 67) this.events.fallDamage = 50;
        else if (delta > 58) this.events.fallDamage = 25;
        else if (delta > 48) this.events.fallDamage = 15;
        else if (delta > 38.75) this.events.fallDamage = 10;
      }
      // the velocity is cleared even on SURF_NODAMAGE surfaces
      if (delta > 38.75) ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
      return;
    }

    // ETJump
    let takesDamage: boolean;
    if (rules.noFallDamage === 2) takesDamage = false;
    else if (rules.noFallDamage === 1) takesDamage = noDamageSurface;
    else takesDamage = !noDamageSurface;
    if (!takesDamage) return;

    // PM_CheckFallDamage
    if (delta > 77) {
      this.events.fallDamage = 9999;
    } else if (delta > 67) {
      this.events.fallDamage = 50;
      this.knockback(1000);
    } else if (delta > 58) {
      this.events.fallDamage = 25;
      this.knockback(250);
    } else if (delta > 48) {
      this.events.fallDamage = 15;
      this.knockback(1000);
    } else if (delta > 38.75) {
      this.events.fallDamage = 10;
      this.knockback(1000);
    }
    if (delta > 38.75) ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
  }

  /** ETJump applies the fall damage knockback inside pmove, on the landing frame. */
  private knockback(time: number): void {
    this.ps.pmTime = time;
    this.ps.pmFlags |= PMF_TIME_KNOCKBACK;
  }

  // --- bg_slidemove.c ---

  private slideMove(gravity: boolean): boolean {
    const { ps, pml } = this;
    let numbumps = 4;
    let extrabumps = 0;
    const primalVelocity = copy(ps.velocity);
    const endVelocity: Vec3 = [0, 0, 0];
    const planes: Vec3[] = [];
    const clipVel: Vec3 = [0, 0, 0];
    const endClipVel: Vec3 = [0, 0, 0];
    const dir: Vec3 = [0, 0, 0];
    const end: Vec3 = [0, 0, 0];

    if (gravity) {
      endVelocity[0] = ps.velocity[0];
      endVelocity[1] = ps.velocity[1];
      endVelocity[2] = f(ps.velocity[2] - f(ps.gravity * pml.frametime));
      ps.velocity[2] = f(f(ps.velocity[2] + endVelocity[2]) * 0.5);
      primalVelocity[2] = endVelocity[2];
      if (pml.groundPlane) {
        // slide along the ground plane
        clipVelocity(ps.velocity, pml.groundTrace.plane.normal, ps.velocity, OVERCLIP_F);
      }
    }

    let timeLeft = pml.frametime;

    // never turn against the ground plane
    if (pml.groundPlane) planes.push(copy(pml.groundTrace.plane.normal));
    // never turn against original velocity
    const pv: Vec3 = [0, 0, 0];
    fnormalize2(ps.velocity, pv);
    planes.push(pv);

    let bumpcount = 0;
    for (; bumpcount < numbumps; bumpcount++) {
      // calculate position we are trying to move to
      fma3(end, ps.origin, timeLeft, ps.velocity);
      // see if we can make it there
      let trace = this.traceAll(ps.origin, end);

      if (trace.allsolid) {
        // entity is completely trapped in another solid
        ps.velocity[2] = 0; // don't build up falling damage, but allow sideways acceleration
        return true;
      }
      if (trace.fraction > 0) {
        // actually covered some distance
        ps.origin = copy(trace.endpos);
      }
      if (trace.fraction === 1) break; // moved the entire distance

      // save entity for contact
      this.addTouchEnt(trace.entityNum);

      timeLeft = f(timeLeft - f(timeLeft * trace.fraction));

      if (planes.length >= MAX_CLIP_PLANES) {
        // this shouldn't really happen
        ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
        return true;
      }

      // if this is the same plane we hit before, nudge velocity out along it,
      // which fixes some epsilon issues with non-axial planes
      let i = 0;
      for (; i < planes.length; i++) {
        if (fdot(trace.plane.normal, planes[i]) > 0.99) {
          if (extrabumps <= 0) {
            for (let k = 0; k < 3; k++) ps.velocity[k] = f(trace.plane.normal[k] + ps.velocity[k]);
            extrabumps++;
            numbumps++;
          } else {
            // zinx - if it happens again, nudge the origin instead, and trace it
            const nudge: Vec3 = [0, 0, 0];
            for (let k = 0; k < 3; k++) nudge[k] = f(ps.origin[k] + trace.plane.normal[k]);
            trace = this.traceAll(ps.origin, nudge);
            ps.origin = copy(trace.endpos);
          }
          break;
        }
      }
      if (i < planes.length) continue;
      planes.push(copy(trace.plane.normal));

      // modify velocity so it parallels all of the clip planes
      // find a plane that it enters
      for (i = 0; i < planes.length; i++) {
        const into = fdot(ps.velocity, planes[i]);
        if (into >= 0.1) continue; // move doesn't interact with the plane
        // see how hard we are hitting things
        if (-into > pml.impactSpeed) pml.impactSpeed = -into;

        // slide along the plane
        clipVelocity(ps.velocity, planes[i], clipVel, OVERCLIP_F);
        clipVelocity(endVelocity, planes[i], endClipVel, OVERCLIP_F);

        // see if there is a second plane that the new move enters
        for (let j = 0; j < planes.length; j++) {
          if (j === i) continue;
          if (fdot(clipVel, planes[j]) >= 0.1) continue; // move doesn't interact with the plane

          // try clipping the move to the plane
          clipVelocity(clipVel, planes[j], clipVel, OVERCLIP_F);
          clipVelocity(endClipVel, planes[j], endClipVel, OVERCLIP_F);

          // see if it goes back into the first clip plane
          if (fdot(clipVel, planes[i]) >= 0) continue;

          // slide the original velocity along the crease
          fcross(dir, planes[i], planes[j]);
          fnormalize(dir);
          let d = fdot(dir, ps.velocity);
          fscale(clipVel, dir, d);
          fcross(dir, planes[i], planes[j]);
          fnormalize(dir);
          d = fdot(dir, endVelocity);
          fscale(endClipVel, dir, d);

          // see if there is a third plane the the new move enters
          for (let k = 0; k < planes.length; k++) {
            if (k === i || k === j) continue;
            if (fdot(clipVel, planes[k]) >= 0.1) continue; // move doesn't interact with the plane
            // stop dead at a triple plane interaction
            ps.velocity[0] = ps.velocity[1] = ps.velocity[2] = 0;
            return true;
          }
        }

        // if we have fixed all interactions, try another move
        ps.velocity = copy(clipVel);
        endVelocity[0] = endClipVel[0];
        endVelocity[1] = endClipVel[1];
        endVelocity[2] = endClipVel[2];
        break;
      }
    }

    if (gravity) ps.velocity = copy(endVelocity);
    // don't change velocity if in a timer (FIXME: is this correct?)
    if (ps.pmTime) ps.velocity = copy(primalVelocity);
    return bumpcount !== 0;
  }

  private stepSlideMove(gravity: boolean): void {
    const { ps } = this;
    const startO = copy(ps.origin);
    const startV = copy(ps.velocity);

    if (!this.slideMove(gravity)) return; // we got exactly where we wanted to go first try
    const downO = copy(ps.origin);
    const downV = copy(ps.velocity);

    const down: Vec3 = [startO[0], startO[1], f(startO[2] - STEPSIZE)];
    let trace = this.traceAll(startO, down);
    // never step up when you still have up velocity
    if (ps.velocity[2] > 0 && (trace.fraction === 1.0 || trace.plane.normal[2] < 0.7)) return;

    const up: Vec3 = [startO[0], startO[1], f(startO[2] + STEPSIZE)];
    // test the player position if they were a stepheight higher
    trace = this.traceAll(up, up);
    if (trace.allsolid) return; // can't step up

    // try slidemove from this position
    ps.origin = copy(up);
    ps.velocity = copy(startV);
    this.slideMove(gravity);

    // push down the final amount
    const down2: Vec3 = [ps.origin[0], ps.origin[1], f(ps.origin[2] - STEPSIZE)];
    // check legs separately
    if (this.prone) {
      trace = this.traceLegs(ps.origin, down2, null, ps.viewangles, false);
      if (trace.allsolid) {
        // legs don't step, just fuzz
        ps.origin = downO;
        ps.velocity = downV;
        return;
      }
    }
    trace = this.trace(ps.origin, down2);
    if (!trace.allsolid) ps.origin = copy(trace.endpos);
    if (trace.fraction < 1.0) clipVelocity(ps.velocity, trace.plane.normal, ps.velocity, OVERCLIP_F);

    const delta = f(ps.origin[2] - startO[2]);
    if (delta > 2) this.events.stepped = delta;
  }
}

function emptyGroundTrace(): TraceResult {
  return {
    allsolid: false,
    startsolid: false,
    fraction: 1,
    endpos: [0, 0, 0],
    plane: { normal: [0, 0, 1], dist: 0 },
    surfaceFlags: 0,
    contents: 0,
    entityNum: ENTITYNUM_NONE,
  };
}

const shared = new Pmove();

/** Runs one PmoveSingle step (cmd.serverTime - ps.commandTime milliseconds). */
export function pmoveSingle(ps: PlayerState, cmd: UserCmd, env: PmoveEnv, rules: MapRules = DEFAULT_RULES): PmoveEvents {
  return shared.run(ps, cmd, env, rules);
}

/** Current collision box for the player state (crouch / prone aware). */
export function playerBounds(ps: PlayerState): { mins: Vec3; maxs: Vec3 } {
  const top = ps.eFlags & EF_PRONE ? ps.maxs[2] - ps.standViewHeight - PRONE_VIEWHEIGHT : ps.pmFlags & PMF_DUCKED ? ps.crouchMaxZ : ps.maxs[2];
  return { mins: copy(ps.mins), maxs: [ps.maxs[0], ps.maxs[1], top] };
}
